import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUser } from './../components/Context/UserContext';
import CountUp from 'react-countup';
import ReactApexChart from 'react-apexcharts';
import profileImg from '../assets/logo.png';
import '../styles/VendorMasterPage.css';
function VendorMasterPage({ children }) {
    const { user } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [darkMode, setDarkMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showCard, setShowCard] = useState(false);
    const [showWelcome, setShowWelcome] = useState(false);
    const [animationPhase, setAnimationPhase] = useState('initial');
    const [stats, setStats] = useState({
        totalInvoices: 0,
        approvedInvoices: 0,
        inProgressInvoices: 0,
        rejectedInvoices: 0,
    });
    // ── same monthly invoice data as MasterPage ──
    const monthlyInvoiceData = [
        { month: 'Jan', approved: 80, pending: 60, totalAmount: 12500000 },
        { month: 'Feb', approved: 60, pending: 80, totalAmount: 9800000 },
        { month: 'Mar', approved: 95, pending: 85, totalAmount: 15200000 },
        { month: 'Apr', approved: 55, pending: 70, totalAmount: 8900000 },
        { month: 'May', approved: 35, pending: 90, totalAmount: 21000000 },
    ];
    // ── fetch vendor-specific stats ──
    useEffect(() => {
        const fetchVendorStats = async () => {
            try {
                setLoading(true);
                const vendorCode = user?.vendorCode || user?.VendorCode;
                if (!vendorCode) return;
                const response = await axios.get(
                    `/api/vendor/invoice-stats/${vendorCode}`
                );
                setStats({
                    totalInvoices: response.data.totalInvoices || 0,
                    approvedInvoices: response.data.approvedInvoices || 0,
                    inProgressInvoices: response.data.inProgressInvoices || 0,
                    rejectedInvoices: response.data.rejectedInvoices || 0,
                });
            } catch (err) {
                console.error('Failed to fetch vendor stats:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchVendorStats();
        const interval = setInterval(fetchVendorStats, 30000);
        return () => clearInterval(interval);
    }, [user]);
    // ── dark mode persistence ──
    useEffect(() => {
        document.body.classList.toggle('dark-mode', darkMode);
        localStorage.setItem('darkMode', darkMode);
    }, [darkMode]);
    // ── welcome animation (same logic as MasterPage) ──
    useEffect(() => {
        const justLoggedIn = location.state?.justLoggedIn;
        if (justLoggedIn && user) {
            setShowWelcome(true);
            setTimeout(() => setAnimationPhase('sliding'), 400);
            setTimeout(() => setAnimationPhase('complete'), 1800);
            setTimeout(() => setShowWelcome(false), 3500);
        }
    }, [location.state, user]);
    const toggleSidebar = () => setSidebarOpen(prev => !prev);
    const toggleDarkMode = () => setDarkMode(prev => !prev);
    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };
    // ── Vendor Activity Overview (same as MasterPage) ──
    const chartOptions = {
        chart: {
            type: 'area',
            height: 350,
            animations: { enabled: true, easing: 'easeinout', speed: 900 },
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        colors: ['#0ea5e9'],
        stroke: { curve: 'smooth', width: 3 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 0.9, opacityFrom: 0.65, opacityTo: 0.15 } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: val => `${Math.round(val)} vendors` } },
        xaxis: {
            categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
            title: { text: 'Month' },
        },
        yaxis: { min: 0, max: 50, title: { text: 'Vendors' } },
        grid: { borderColor: darkMode ? '#334155' : '#e2e8f0', strokeDashArray: 4 },
    };
    const chartSeries = [{ name: 'Active Vendors Trend', data: [28, 25, 18, 22, 31, 38, 42] }];
    // ── Monthly Invoice bar chart (same as MasterPage) ──
    const invoiceBarOptions = {
        chart: {
            type: 'bar',
            height: 350,
            toolbar: { show: false },
            animations: { enabled: true, easing: 'easeinout', speed: 800 },
        },
        colors: ['#1f77b4', '#ff7f0e', '#d62728'],
        plotOptions: {
            bar: { horizontal: false, columnWidth: '45%', borderRadius: 4 },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 1, colors: ['transparent'] },
        xaxis: {
            categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
            labels: { style: { colors: darkMode ? '#e2e8f0' : '#64748b', fontSize: '12px' } },
        },
        yaxis: [
            {
                title: { text: 'Number of Invoices', style: { color: darkMode ? '#e2e8f0' : '#64748b' } },
                labels: { style: { colors: darkMode ? '#e2e8f0' : '#64748b' } },
            },
            {
                opposite: true,
                title: { text: 'Total Amount (₹ Lakhs)', style: { color: '#d62728' } },
                labels: { style: { colors: '#d62728' } },
            },
        ],
        fill: { opacity: 0.95 },
        tooltip: {
            shared: true,
            intersect: false,
            y: {
                formatter: (val, { seriesIndex }) =>
                    seriesIndex === 2 ? `₹ ${val.toLocaleString()} Lakhs` : `${val} invoices`,
            },
        },
        legend: {
            position: 'top',
            horizontalAlign: 'center',
            labels: { colors: darkMode ? '#e2e8f0' : '#64748b' },
        },
        grid: { borderColor: darkMode ? '#334155' : '#e2e8f0', strokeDashArray: 4 },
    };
    const invoiceBarSeries = [
        { name: 'Approved Invoices', data: monthlyInvoiceData.map(m => m.approved) },
        { name: 'Pending Invoices', data: monthlyInvoiceData.map(m => m.pending) },
        { name: 'Total Amount (₹ Lakhs)', data: monthlyInvoiceData.map(m => m.totalAmount / 100000) },
    ];
    const vendorCards = [
        {
            title: 'Total Invoices Created',
            value: stats.totalInvoices,
            color: '#2563eb',
            icon: '🧾',
            bg: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
        },
        {
            title: 'Approved Invoices',
            value: stats.approvedInvoices,
            color: '#16a34a',
            icon: '✅',
            bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
        },
        {
            title: 'In Progress Invoices',
            value: stats.inProgressInvoices,
            color: '#d97706',
            icon: '⏳',
            bg: 'linear-gradient(135deg, #fef9c3 0%, #fde68a 100%)',
        },
        {
            title: 'Rejected Invoices',
            value: stats.rejectedInvoices,
            color: '#dc2626',
            icon: '❌',
            bg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
        },
    ];
    if (!user) return <div className="vendor-loading">Loading...</div>;
    return (
        <>
            {/* Welcome Overlay */}
            {showWelcome && (
                <div className="vendor-welcome-overlay">
                    {animationPhase === 'complete' && (
                        <div className="vendor-welcome-message fade-in">
                            Welcome to the Vendor Portal
                        </div>
                    )}
                </div>
            )}
            <div className={`vendor-master-container ${darkMode ? 'dark' : ''} ${showWelcome ? 'blurred' : ''}`}>
                {/* ── Navbar ── */}
                <div className="vendor-navbar glass">
                    <div className="vendor-navbar-left">
                        <button className="vendor-toggle-btn" onClick={toggleSidebar}>☰</button>
                        <span className="vendor-portal-badge">Vendor Portal</span>
                    </div>
                    <div className="vendor-navbar-right">
                        <Link to="/vendor/dashboard">Dashboard</Link>
                        <Link to="/vendor/registration">Registration</Link>
                        <Link to="/vendor/invoices">Invoices</Link>
                        <button className="dark-toggle" onClick={toggleDarkMode}>
                            {darkMode ? '☀ Light' : '🌙 Dark'}
                        </button>
                        <button className="vendor-logout-btn" onClick={handleLogout}>
                            Logout
                        </button>
                        <div className="vendor-profile-wrapper" onClick={() => setShowCard(prev => !prev)}>
                            <img src={profileImg} className="vendor-profile-img" alt="Profile" />
                        </div>
                    </div>
                </div>
                {/* ── Profile Card ── */}
                {showCard && (
                    <div className="vendor-info-card">
                        <button className="vendor-card-close" onClick={() => setShowCard(false)}>×</button>
                        <div className="vendor-card-avatar">
                            <img src={profileImg} alt="avatar" />
                        </div>
                        <div className="vendor-card-name">{user?.vendorName || user?.VendorName || 'Vendor'}</div>
                        <div className="vendor-card-code">Code: {user?.vendorCode || user?.VendorCode || '—'}</div>
                        <div className="vendor-card-email">{user?.email || user?.Email || '—'}</div>
                    </div>
                )}
                {/* ── Sidebar ── */}
                <div className={`vendor-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
                    <div className="vendor-sidebar-header">
                        <span>Menu</span>
                    </div>
                    <ul>
                        <li>
                            <Link to="/vendor/dashboard">
                                <span className="sidebar-icon">📊</span>
                                <span className="sidebar-label">Dashboard</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/vendor/registration">
                                <span className="sidebar-icon">📝</span>
                                <span className="sidebar-label">Registration</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/vendor/invoices">
                                <span className="sidebar-icon">🧾</span>
                                <span className="sidebar-label">Invoices</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/vendor/profile">
                                <span className="sidebar-icon">👤</span>
                                <span className="sidebar-label">My Profile</span>
                            </Link>
                        </li>
                    </ul>
                </div>
                {/* ── Main Content ── */}
                <div className={`vendor-main-content ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
                    {/* Greeting */}
                    <div className="vendor-greeting">
                        <h2>Hello, {user?.vendorName || user?.VendorName || 'Vendor'} 👋</h2>
                        <p>Here's a summary of your activity</p>
                    </div>
                    {/* ── Stat Cards ── */}
                    <div className="vendor-cards-container">
                        {vendorCards.map((card, i) => (
                            <div
                                key={card.title}
                                className="vendor-stat-card"
                                style={{ background: card.bg, animationDelay: `${i * 0.12}s` }}
                            >
                                <div className="vendor-card-icon">{card.icon}</div>
                                <div className="vendor-card-info">
                                    <h4>{card.title}</h4>
                                    {loading ? (
                                        <div className="vendor-skeleton" />
                                    ) : (
                                        <div className="vendor-stat-number" style={{ color: card.color }}>
                                            <CountUp end={card.value} duration={1.8} separator="," />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* ── Page-specific children ── */}
                    {children && (
                        <div className="vendor-page-content">
                            {children}
                        </div>
                    )}
                    {/* ── Vendor Activity Overview (same as MasterPage) ── */}
                    <div className="vendor-chart-card" style={{ animationDelay: '0.6s' }}>
                        <div className="vendor-chart-header">
                            <h3>Vendor Activity Overview</h3>
                            <span className="live-badge">LIVE</span>
                        </div>
                        <ReactApexChart
                            options={chartOptions}
                            series={chartSeries}
                            type="area"
                            height={350}
                        />
                    </div>
                    {/* ── Monthly Invoice Chart (same as MasterPage) ── */}
                    <div className="vendor-chart-card" style={{ animationDelay: '0.8s', marginTop: '24px' }}>
                        <div className="vendor-chart-header">
                            <h3>Invoice Details (Monthly)</h3>
                            <span className="live-badge">LIVE</span>
                        </div>
                        <ReactApexChart
                            options={invoiceBarOptions}
                            series={invoiceBarSeries}
                            type="bar"
                            height={350}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
export default VendorMasterPage;